// Wire mirrors of the broker-to-exchange receivable surface (SM-771,
// SM-621 phase 06b) — contracts httpapi/v1/broker_exposure.go and the
// vocabulary in contracts broker/v1/exposure.go. These are the console's
// copy of shapes broker-service owns, so a field's comment says what an
// operator does with it.
//
// AMOUNTS HERE ARE CANONICAL 8dp integer strings of ONE currency
// (1 GOLD = "100000000") — NOT the 4dp wire strings the rest of the
// console carries. Nothing may hand one to a money component without
// going through `canonToWire` (components/brokers/receivables.ts).

import type { PageInfo } from "./types";

/**
 * OPEN and ACKNOWLEDGED are unresolved — at most one per root broker and
 * currency — and block new exposure in that broker's whole tree until
 * resolved. SETTLED and WRITTEN_OFF are terminal.
 */
export type ReceivableState = "OPEN" | "ACKNOWLEDGED" | "SETTLED" | "WRITTEN_OFF";

export const RECEIVABLE_STATES: ReceivableState[] = [
  "OPEN",
  "ACKNOWLEDGED",
  "SETTLED",
  "WRITTEN_OFF",
];

/** The list filter's pseudo-state: OPEN + ACKNOWLEDGED. */
export const RECEIVABLE_UNRESOLVED = "UNRESOLVED";

/**
 * How a receivable was resolved. The first two are the shortfall
 * worker's own reassessment; the last two are an operator action an
 * admin approved — the requester included (SM-946).
 */
export type ReceivableResolutionPath =
  | "COLLATERAL_TOPUP"
  | "NET_POSITION_RECOVERED"
  | "EXTERNAL_SETTLEMENT"
  | "WRITE_OFF";

export type ReceivableActionKind = "ACKNOWLEDGE" | "SETTLE" | "WRITE_OFF";

/** APPLIED is an acknowledge (no checker); the rest are maker-checker. */
export type ReceivableActionState = "PENDING" | "APPROVED" | "REJECTED" | "APPLIED";

export interface ReceivableActionItem {
  action_id: string;
  receivable_id: string;
  kind: ReceivableActionKind;
  state: ReceivableActionState;
  /** The external payment reference a settle names. */
  reference?: string;
  /**
   * The confirmed INBOUND settlement note a settle uses (SM-948); its
   * reference is then the note's. Absent on a reference-only settle.
   */
  settlement_note_id?: string;
  reason: string;
  requested_by: string;
  requested_at_unix_ms: number;
  decided_by?: string;
  decided_at_unix_ms?: number;
  decision_reason?: string;
}

/** One event row the shortfall worker cites as moving the net position. */
export interface ReceivableEvidenceEvent {
  event_id?: string;
  kind?: string;
  ref_id?: string;
  delta_net?: string;
  applied_at?: string | number;
  applied_at_unix_ms?: number;
}

/**
 * What the shortfall worker recorded when it last assessed the root. The
 * wire type is free JSON (json.RawMessage); these are the fields the
 * worker writes, all optional so an older or partial record still
 * renders — the raw JSON is always one click away.
 */
export interface ReceivableEvidence {
  assessed_at_unix_ms?: number;
  net_position?: string;
  owed?: string;
  collateral?: string;
  covered_by_resolutions?: string;
  uncovered?: string;
  recent_events?: ReceivableEvidenceEvent[];
  [key: string]: unknown;
}

export interface ReceivableItem {
  receivable_id: string;
  /** The ROOT broker the receivable belongs to. */
  broker_id: string;
  currency: string;
  state: ReceivableState;
  /** Uncovered now while unresolved; frozen at resolution. Never revenue. */
  amount: string;
  peak_amount: string;
  owed: string;
  collateral: string;
  evidence?: ReceivableEvidence | null;
  owner_admin_id?: string;
  resolution_path?: ReceivableResolutionPath;
  resolution_reference?: string;
  /** The settlement note that settled it (SM-948); only when SETTLED through a note. */
  settlement_note_id?: string;
  opened_at_unix_ms: number;
  updated_at_unix_ms: number;
  resolved_at_unix_ms?: number;
  actions?: ReceivableActionItem[];
}

export interface ReceivableListResponse {
  items: ReceivableItem[];
  page: PageInfo;
}

/** The two broker fields this page shows; the full item is BrokerItem. */
export interface BrokerSummary {
  broker_id: string;
  display_name?: string;
  legal_name?: string;
  status?: string;
}

// ── broker payout batches (SM-710, SM-621 phase 07a) ─────────────────
//
// Wire mirrors of contracts httpapi/v1/broker_revenue.go (payout batches)
// and the vocabulary in contracts broker/v1/revenue.go. Amounts are
// CANONICAL 8dp integer strings of ONE currency, like the receivables
// above — through `canonToWire` before any money component — and a
// batch is always one currency: nothing here is ever summed across
// currencies.

/**
 * DRAFT → PENDING_REVIEW (escrow posted) → APPROVED (enough approvals) →
 * PROCESSING (finance is paying) → PAID (confirmed, escrow burned).
 * REJECTED / CANCELLED / FAILED restore the escrow once; all four
 * right-hand states are terminal (SM-708 §7.3).
 */
export type PayoutBatchState =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "APPROVED"
  | "PROCESSING"
  | "PAID"
  | "FAILED"
  | "REJECTED"
  | "CANCELLED";

export const PAYOUT_BATCH_STATES: PayoutBatchState[] = [
  "DRAFT",
  "PENDING_REVIEW",
  "APPROVED",
  "PROCESSING",
  "PAID",
  "FAILED",
  "REJECTED",
  "CANCELLED",
];

/** WEEKLY / MONTHLY are the scheduler's; MANUAL an operator's batch. */
export type PayoutSchedule = "WEEKLY" | "MONTHLY" | "NONE" | "MANUAL";

export interface PayoutApprovalItem {
  approver_id: string;
  decision: "APPROVE" | "REJECT";
  reason?: string;
  decided_at_unix_ms: number;
}

export interface PayoutBatchItem {
  batch_id: string;
  broker_id: string;
  root_broker_id: string;
  currency: string;
  schedule: PayoutSchedule;
  period_from_unix_ms?: number;
  cutoff_unix_ms: number;
  /** A pre-SM-881 scheduled batch's minimum; omitted when zero (every admin-prepared batch, SM-912). */
  minimum?: string;
  total: string;
  item_count: number;
  state: PayoutBatchState;
  /**
   * An admin id, or "system:scheduler" for an old scheduled batch. A batch
   * carries no destination (SM-878 / SM-882): Smart Market stores none.
   */
  requested_by: string;
  request_reason?: string;
  approvals_required: number;
  approvals: PayoutApprovalItem[];
  executed_by?: string;
  finalized_by?: string;
  external_reference?: string;
  rail_reference?: string;
  failure_reason?: string;
  escrow_operation_id: string;
  restore_operation_id?: string;
  paid_operation_id?: string;
  /**
   * The ledger confirmed that step. An operation id without its posted flag
   * is a money step still in flight (broker-service re-drives it with the
   * same id); attempts / last_error describe the latest try.
   */
  escrow_posted: boolean;
  restore_posted?: boolean;
  paid_posted?: boolean;
  attempts?: number;
  last_error?: string;
  created_at_unix_ms: number;
  reviewed_at_unix_ms?: number;
  executed_at_unix_ms?: number;
  finalized_at_unix_ms?: number;
}

export interface PayoutBatchListResponse {
  items: PayoutBatchItem[];
  page: PageInfo;
}

/** One audited transition (the batch's own or one of its earnings'). */
export interface EarningEventItem {
  event_id: string;
  action: string;
  from_state?: string;
  to_state?: string;
  actor_kind: string;
  actor_id?: string;
  reason?: string;
  reference?: string;
  occurred_at_unix_ms: number;
}

/** The supporting earnings' fields the batch panel shows (contracts EarningItem). */
export interface PayoutBatchEarning {
  earning_id: string;
  recipient_broker_id: string;
  origin: string;
  source_kind?: string;
  /** The fill or redemption the commission came from. */
  source_id?: string;
  role?: string;
  currency: string;
  /** The whole commission this earning is a share of (canonical 8dp). */
  commission_total?: string;
  share_bps?: number;
  /** Canonical 8dp; negative for a debit adjustment. */
  amount: string;
  /** The earning's CURRENT state, not the one it had when the batch took it. */
  state: string;
  held: boolean;
  hold_reason?: string;
  /** The batch holding it now — a later one once this batch restored it. */
  batch_id?: string;
  /** The adjustment that created an ADJUSTMENT earning. */
  adjustment_id?: string;
  /** The reassignment that created a REASSIGNMENT earning. */
  reassignment_id?: string;
  created_at_unix_ms: number;
  released_at_unix_ms?: number;
  /** When the earning was paid (its batch confirmed PAID). */
  paid_at_unix_ms?: number;
}

/** One audited batch action: actor, reason, and the batch's totals then (SM-710 AC 4). */
export interface PayoutBatchEventItem extends EarningEventItem {
  total: string;
  item_count: number;
}

export interface PayoutBatchDetail {
  batch: PayoutBatchItem;
  items: PayoutBatchEarning[];
  page: PageInfo;
  events: PayoutBatchEventItem[];
}

/** A batch action's inputs: a reason always; references to complete. */
export interface PayoutBatchActionRequest {
  reason: string;
  external_reference?: string;
  rail_reference?: string;
}

export type PayoutBatchAction = "approve" | "reject" | "cancel" | "execute" | "complete" | "fail";

// ── broker governance (SM-716, SM-621 phase 08a) ──────────────────────
//
// Wire mirrors of contracts httpapi/v1/broker.go (organizations, staff,
// hierarchy, accounts, audit), broker_funding.go (pools, transfers, the
// funding reconciliation), broker_exposure.go (exposure views and limit
// terms), broker_revenue.go (revenue reconciliation) and
// broker_portal.go (integration status, the investigation trace), with
// the vocabulary of contracts broker/v1. Amounts are CANONICAL 8dp
// integer strings of ONE currency — through `canonToWire` before any
// money component — and nothing here is ever summed across currencies.

/**
 * A node's OWN lifecycle status (B10):
 *   DRAFT ─approve→ ACTIVE ─pause→ PAUSED ─resume→ ACTIVE
 *   ACTIVE | PAUSED ─begin-close→ CLOSING ─close→ CLOSED
 *   DRAFT ─close→ CLOSED (a never-approved node is discarded)
 * The EFFECTIVE status is the most restrictive own status on the node's
 * root→node path.
 */
export type BrokerStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "CLOSING" | "CLOSED";

export const BROKER_STATUSES: BrokerStatus[] = ["DRAFT", "ACTIVE", "PAUSED", "CLOSING", "CLOSED"];

/** One broker node (httpapi BrokerItem). JSON documents are objects. */
/**
 * SM-978 / SM-984: contracts/httpapi/v1 BrokerCurrencyImageResponse — a
 * broker's GOLD image. effective = own ?? inherited; null = the platform's
 * Gold. Nullable fields arrive as null. Brokers upload it on the portal;
 * operators see it and may remove it.
 */
export interface BrokerCurrencyImageResponse {
  currency: string;
  own_image_url: string | null;
  effective_image_url: string | null;
  inherited_image_url: string | null;
  inherited_from_broker_id: string | null;
  inherited_from_display_name: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_kind: string | null;
  updated_by_name: string | null;
}

export interface BrokerItem {
  broker_id: string;
  parent_broker_id?: string;
  root_broker_id: string;
  broker_depth: number;
  status: BrokerStatus;
  status_reason?: string;
  legal_name: string;
  display_name: string;
  contacts?: Record<string, unknown> | null;
  payout_info?: Record<string, unknown> | null;
  business_settings?: Record<string, unknown> | null;
  settings_version: number;
  hierarchy_version: number;
  row_version: number;
  is_system?: boolean;
  created_by?: string;
  created_at_unix_ms: number;
  updated_at_unix_ms: number;
  status_changed_at_unix_ms: number;
  /** Set on reads that resolve the path (detail, descendants). */
  effective_status?: BrokerStatus;
  /** Direct children; set only on the reads that list children. */
  child_count?: number;
}

/** POST /admin/brokers (SM-963): a new organisation has no parent_broker_id. Always created as DRAFT. */
export interface CreateBrokerRequest {
  parent_broker_id?: string;
  display_name: string;
  legal_name: string;
  reason: string;
}

export interface BrokerListResponse {
  items: BrokerItem[];
  page: PageInfo;
}

/** approve / pause / resume / begin-close / close. */
export type LifecycleTransition = "approve" | "pause" | "resume" | "begin-close" | "close";

/** EMERGENCY is accepted on pause only: the emergency disable (D64). */
export type LifecycleReasonKind = "ROUTINE" | "EMERGENCY";

export interface LifecycleTransitionRequest {
  reason: string;
  /** Makes the write conditional on the row the operator read. */
  expected_row_version?: number;
  reason_kind?: LifecycleReasonKind;
}

/** One hop of an ordered root→node path. */
export interface BrokerPathNode {
  broker_id: string;
  broker_depth: number;
}

/** GET /admin/brokers/{id}/path — the node's live resolved state. */
export interface EffectiveBrokerItem {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  broker_depth: number;
  status: BrokerStatus;
  effective_status: BrokerStatus;
  /** The node on the path that imposes the effective status. */
  restricting_broker_id: string;
  hierarchy_version: number;
  settings_version: number;
  is_system?: boolean;
  path: BrokerPathNode[];
  /**
   * The currencies the node trades (SM-868), GOLD, RED, GREEN order:
   * ["GOLD"] on every broker node, all three on the SYSTEM_BROKER.
   * Absent from a broker-service without SM-868 — nodeCurrencies()
   * then applies that rule from is_system.
   */
  currencies?: string[];
}

export type StaffRole = "OWNER" | "ADMINISTRATOR" | "FINANCE" | "TRADER" | "VIEWER";

export const STAFF_ROLES: StaffRole[] = ["OWNER", "ADMINISTRATOR", "FINANCE", "TRADER", "VIEWER"];

export type StaffMembershipStatus = "INVITED" | "ACTIVE" | "SUSPENDED" | "REMOVED";

export interface StaffMembershipItem {
  membership_id: string;
  broker_id: string;
  /** Deprecated (SM-968): the raw principal; show the staff account below instead. */
  user_id: string;
  role: StaffRole;
  status: StaffMembershipStatus;
  invited_by: string;
  invited_by_kind?: string;
  invited_at_unix_ms: number;
  activated_at_unix_ms?: number;
  suspended_at_unix_ms?: number;
  removed_at_unix_ms?: number;
  row_version: number;
  /** The member's staff account (SM-968); empty on a pre-SM-968 customer-id row. */
  staff_id?: string;
  email?: string;
  display_name?: string;
  account_status?: StaffAccountStatus;
  /** The inviting staff member's name when a broker's staff invited. */
  invited_by_name?: string;
}

/** A staff account's state (SM-968): PENDING has not set a password yet. */
export type StaffAccountStatus = "PENDING" | "ACTIVE" | "SUSPENDED" | "CLOSED";

export interface StaffMembershipListResponse {
  items: StaffMembershipItem[];
  page: PageInfo;
}

/** Staff are invited by email (SM-968); broker-service emails them a set-password link. */
export interface InviteStaffRequest {
  email: string;
  role: StaffRole;
  reason?: string;
}

/** suspend / resume / remove. */
export type StaffCommand = "suspend" | "resume" | "remove";

/** One audit row: who changed what, from what, to what, why, when. */
export interface BrokerAuditEventItem {
  event_id: string;
  broker_id: string;
  entity_kind: string;
  entity_id: string;
  action: string;
  actor_kind: string;
  actor_id: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
  request_id?: string;
  occurred_at_unix_ms: number;
}

export interface BrokerAuditListResponse {
  items: BrokerAuditEventItem[];
  page: PageInfo;
}

/**
 * The kinds of obligation that stand between a node and CLOSED (and a
 * move), across the phases that register them.
 */
export type ObligationKind =
  | "DESCENDANTS"
  | "UNPAID_REVENUE"
  | "OPEN_ACCOUNTS"
  | "AVAILABLE_BALANCE"
  | "RESERVED_FUNDS"
  | "PENDING_FUNDING"
  | "OPEN_POSITIONS"
  | "PENDING_REDEMPTIONS"
  | "FUNDING_POOL_BALANCE"
  | "FUNDING_IN_FLIGHT";

/** What an operator does to clear one obligation. */
export type ClearingAction =
  | "CLOSE_OR_MOVE_DESCENDANTS"
  | "PAY_OUT_OR_REASSIGN"
  | "CLOSE_ACCOUNTS"
  | "EMPTY_BALANCE"
  | "CANCEL_OPEN_ORDERS"
  | "AWAIT_PENDING_OPERATION"
  | "CLOSE_OR_REDEEM_POSITIONS"
  | "RETURN_TO_PARENT"
  | "PAY_OUT_MASTER";

/**
 * One thing standing between a broker and CLOSED. Money obligations
 * carry currency + amount (canonical, one entry per currency, never
 * summed); count obligations carry count and a bounded subject list.
 */
export interface BlockingObligation {
  kind: ObligationKind | string;
  currency?: string;
  amount?: string;
  count?: number;
  subject_ids?: string[];
  clearing_action: ClearingAction | string;
}

/** The broker error envelope's inner object (httpapi BrokerErrorDetail). */
export interface BrokerErrorDetail {
  code: string;
  message: string;
  details?: Record<string, string>;
  request_id?: string;
  /** Set only on a CLOSE refusal (BROKER_CLOSE_BLOCKED). */
  blocking?: BlockingObligation[];
}

// ── serviced accounts ──

export type BalanceMode = "SYSTEM_BROKER" | "BROKER_FUNDED" | "THIRD_PARTY_CREDIT";

export const BALANCE_MODES: BalanceMode[] = ["SYSTEM_BROKER", "BROKER_FUNDED", "THIRD_PARTY_CREDIT"];

export type BrokerAccountStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";

export const BROKER_ACCOUNT_STATUSES: BrokerAccountStatus[] = ["ACTIVE", "SUSPENDED", "CLOSED"];

/** direct = accounts the node services itself; subtree = its whole tree. */
export type AccountScope = "direct" | "subtree";

export interface BrokerAccountItem {
  broker_account_id: string;
  user_id: string;
  broker_id: string;
  root_broker_id: string;
  broker_path_snapshot_id: string;
  balance_mode: BalanceMode;
  status: BrokerAccountStatus;
  entry_path: string;
  referral?: unknown;
  consent?: unknown;
  status_reason?: string;
  row_version: number;
  created_at_unix_ms: number;
  updated_at_unix_ms: number;
  closed_at_unix_ms?: number;
  effective_status?: BrokerStatus;
}

export interface BrokerAccountListResponse {
  items: BrokerAccountItem[];
  page: PageInfo;
}

// ── funding (SM-697) ──

/** MASTER is a root's rail-facing account; POOL every other node's. */
export type FundingRole = "MASTER" | "POOL";

/** One node account in one currency, as the ledger holds it at read time. */
export interface FundingPoolBalance {
  broker_id: string;
  root_broker_id: string;
  role: FundingRole;
  currency: string;
  account_code: string;
  wallet_id: string;
  status: string;
  available: string;
  reserved: string;
  pending_withdrawal: string;
  can_spend: boolean;
  can_receive: boolean;
}

export interface FundingPoolListResponse {
  broker_id: string;
  root_broker_id: string;
  pools: FundingPoolBalance[];
  as_of_unix_ms: number;
}

export type FundingTransferKind = "POOL_ALLOCATION" | "POOL_RETURN" | "USER_ALLOCATION" | "USER_RETURN";

export type FundingTransferState = "PENDING" | "COMMITTED" | "FAILED" | "MANUAL_REVIEW";

export type FundingActorKind = "BROKER_CREDENTIAL" | "EXCHANGE_ADMIN" | "BROKER_STAFF";

export interface FundingTransferItem {
  transfer_id: string;
  kind: FundingTransferKind;
  state: FundingTransferState;
  source_broker_id?: string;
  destination_broker_id?: string;
  broker_account_id?: string;
  currency: string;
  amount: string;
  operation_id: string;
  external_reference?: string;
  actor_kind: FundingActorKind;
  actor_id: string;
  ledger_transfer_id?: string;
  failure_code?: string;
  attempts: number;
  created_at_unix_ms: number;
  settled_at_unix_ms?: number;
  replayed?: boolean;
}

/** Newest first; next_cursor pages further back. */
export interface FundingTransferListResponse {
  items: FundingTransferItem[];
  next_cursor?: string;
}

/**
 * Equation E1 for one (root, currency):
 * top_ups − payouts_completed = node_total + user_allocations − user_returns.
 */
export interface FundingReconRootSummary {
  root_broker_id: string;
  currency: string;
  top_ups: string;
  payouts_completed: string;
  payouts_open: string;
  node_total: string;
  user_allocations: string;
  user_returns: string;
  nodes: number;
  balanced: boolean;
}

/** One broken rule, E1…E6 (SM-692 §7). */
export interface FundingReconFinding {
  rule: string;
  severity: string;
  root_broker_id?: string;
  broker_id?: string;
  currency?: string;
  operation_id?: string;
  transfer_id?: string;
  expected?: string;
  actual?: string;
  detail: string;
}

/** OK (no findings), FINDINGS, or FAILED (a source could not be read). */
export interface FundingReconRunItem {
  run_id: string;
  trigger: string;
  status: string;
  started_at_unix_ms: number;
  finished_at_unix_ms?: number;
  roots: FundingReconRootSummary[];
  findings: FundingReconFinding[];
  error?: string;
}

// ── revenue reconciliation (SM-711) ──

/** FAILED = a source was unreadable; nothing was judged. */
export type RevenueReconState = "RUNNING" | "MATCHED" | "MISMATCH" | "FAILED";

export interface RevenueReconItem {
  broker_id: string;
  currency: string;
  ledger_pending: string;
  earnings_pending: string;
  ledger_available: string;
  earnings_available: string;
  matched: boolean;
  /** A money step was unconfirmed when the run read it: not judged. */
  in_flight?: boolean;
}

export interface RevenueReconEscrowItem {
  currency: string;
  ledger_escrow: string;
  batches_reserved: string;
  matched: boolean;
  in_flight?: boolean;
}

export interface RevenueReconRunItem {
  run_id: string;
  state: RevenueReconState;
  as_of_unix_ms: number;
  triggered_by: string;
  started_at_unix_ms: number;
  finished_at_unix_ms?: number;
  /** Omitted on the list; present on the single-run read. */
  items?: RevenueReconItem[];
  escrow?: RevenueReconEscrowItem[];
  mismatches: number;
  error?: string;
}

export interface RevenueReconRunListResponse {
  items: RevenueReconRunItem[];
  page: PageInfo;
}

// ── exposure (SM-768) ──

export type ExposureBlockReason = "HEADROOM_EXHAUSTED" | "RECEIVABLE_OPEN" | "LIFECYCLE";

/**
 * One node's exposure picture in one currency, computed at read time.
 * limit / headroom / utilisation exist only when the node has its own
 * limit; effective_headroom is the binding node's on the path.
 */
export interface ExposureView {
  broker_id: string;
  root_broker_id: string;
  currency: string;
  effective_status?: string;
  own_exposure: string;
  subtree_exposure: string;
  open_entries: number;
  /** Σ open_entries over the node and every descendant (SM-874); absent from an older server. */
  subtree_open_entries?: number;
  collateral: string;
  has_own_limit: boolean;
  allowance?: string;
  allowance_expires_at_unix_ms?: number;
  limit?: string;
  headroom?: string;
  utilisation_bps?: number;
  warning_threshold_bps?: number;
  effective_headroom?: string;
  binding_broker_id?: string;
  warning: boolean;
  blocked: boolean;
  blocked_reasons?: ExposureBlockReason[];
  net_position?: string;
  owed?: string;
  covered_by_resolutions?: string;
  uncovered?: string;
  receivables_open: number;
  receivable_open_amount?: string;
  as_of_unix_ms: number;
}

export interface ExposureListResponse {
  items: ExposureView[];
  page: PageInfo;
}

/** A node's configured limit terms in one currency. */
export interface ExposureLimitItem {
  broker_id: string;
  currency: string;
  limit_enabled: boolean;
  allowance: string;
  allowance_expires_at_unix_ms?: number;
  allowance_reason?: string;
  allowance_requested_by?: string;
  allowance_approved_by?: string;
  warning_threshold_bps: number;
  updated_by?: string;
  updated_at_unix_ms?: number;
  /** false = no row exists and the defaults apply. */
  configured: boolean;
}

export interface ExposureLimitRequestItem {
  request_id: string;
  broker_id: string;
  currency: string;
  state: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  allowance: string;
  allowance_expires_at_unix_ms?: number;
  limit_enabled?: boolean;
  warning_threshold_bps?: number;
  reason: string;
  requested_by: string;
  requested_at_unix_ms: number;
  decided_by?: string;
  decided_at_unix_ms?: number;
  decision_reason?: string;
}

export interface ExposureLimitResponse {
  limit: ExposureLimitItem;
  pending: ExposureLimitRequestItem[];
}

/** The PUT body: absent fields stay as they are; reason is required. */
export interface ExposureLimitChangeRequest {
  currency: string;
  /** Canonical 8dp. */
  allowance?: string;
  /** 0 removes the expiry. */
  allowance_expires_at_unix_ms?: number;
  warning_threshold_bps?: number;
  /** Non-root nodes only. */
  limit_enabled?: boolean;
  reason: string;
}

/** A limit change's or decision's outcome: applied at once, or a PENDING request (`request` set). */
export interface ExposureLimitChangeResponse {
  applied: boolean;
  limit: ExposureLimitItem;
  request?: ExposureLimitRequestItem;
}

// ── integration status and investigations (SM-716) ──

/**
 * CONFIGURED once the broker has a provider configuration (SM-634);
 * configured does not mean enabled.
 */
export type IntegrationStatus = "NOT_CONFIGURED" | "CONFIGURED";

export interface BrokerIntegrationItem {
  broker_id: string;
  root_broker_id: string;
  display_name: string;
  broker_depth: number;
  status: BrokerStatus;
  integration_status: IntegrationStatus;
  provider_configured: boolean;
  provider_mode: string;
  third_party_provider: boolean;
  external_credit_accounts: number;
}

export interface BrokerIntegrationListResponse {
  items: BrokerIntegrationItem[];
  page: PageInfo;
}

export type InvestigationSubjectKind = "USER" | "BROKER_ACCOUNT" | "ORDER" | "TRADE" | "PAYOUT_BATCH";

export const INVESTIGATION_SUBJECT_KINDS: InvestigationSubjectKind[] = [
  "USER",
  "BROKER_ACCOUNT",
  "ORDER",
  "TRADE",
  "PAYOUT_BATCH",
];

/** user → account → order → fill → fee → revenue → transfer → payout. */
export type InvestigationStepKind = "ACCOUNT" | "ORDER" | "FILL" | "FEE" | "EARNING" | "TRANSFER" | "PAYOUT";

/** One hop of a trace; parent_id names the step it follows from. */
export interface InvestigationStep {
  kind: InvestigationStepKind | string;
  id: string;
  parent_id?: string;
  occurred_at_unix_ms: number;
  user_id?: string;
  broker_id?: string;
  broker_account_id?: string;
  market_id?: string;
  currency?: string;
  amount?: string;
  status?: string;
  summary?: string;
  source: string;
  reference?: string;
}

/** A source the trace could not read. */
export interface InvestigationGap {
  source: string;
  code: string;
  message: string;
}

export interface InvestigationTrace {
  subject_kind: InvestigationSubjectKind;
  subject_id: string;
  user_id?: string;
  accounts: BrokerAccountItem[];
  steps: InvestigationStep[];
  gaps: InvestigationGap[];
  /** A per-kind bound cut the trail. */
  truncated: boolean;
  generated_at_unix_ms: number;
}

// --- settlement position and out-of-band settlement notes (SM-621 phase
// 09a — SM-796 / SM-798 / SM-799; contracts httpapi/v1/broker_settlement.go
// and broker/v1/settlement.go). Canonical 8dp integer strings of ONE
// currency, signed where the figure is signed: positive = we owe the
// broker, negative = the broker owes us (B12). broker-service computes
// every figure; the console only renders them. ---

export type SettlementPositionDirection = "WE_OWE_BROKER" | "BROKER_OWES_US" | "SETTLED";
export type SettlementNoteDirection = "INBOUND" | "OUTBOUND";
export type SettlementNoteStatus = "PENDING" | "CONFIRMED";
export type SettlementScope = "node" | "subtree";

export interface SettlementPositionFigures {
  revenue_released: string;
  revenue_paid: string;
  clearing_delta: string;
  clearing_delta_direction: SettlementPositionDirection;
  period_net: string;
  period_direction: SettlementPositionDirection;
  revenue_available: string;
  clearing_balance: string;
  clearing_balance_direction: SettlementPositionDirection;
  gross: string;
  gross_direction: SettlementPositionDirection;
  confirmed_inbound: string;
  confirmed_outbound: string;
  pending_inbound: string;
  pending_outbound: string;
  outstanding: string;
  direction: SettlementPositionDirection;
  outstanding_if_pending_clear: string;
  if_pending_clear_direction: SettlementPositionDirection;
  collateral: string;
  uncovered: string;
  transfer_in_required: string;
  note_count: number;
  pending_note_count: number;
  has_activity: boolean;
  clearing_account: boolean;
  // ── display-only figures (SM-878: S4 / S5 / S6). Each is optional: an
  // older broker-service sends none, and the users' split is absent when
  // it does not apply (not THIRD_PARTY_CREDIT, a past period, or the
  // settlement-service read failed). None of them is inside another
  // figure, and the console never adds one into anything. ──
  /** Commission already in open payout batches (PAYOUT_RESERVED). */
  revenue_in_payout?: string;
  /**
   * External credit owed, signed (B12): clearing_balance + confirmed
   * inbound − confirmed outbound. What a payment request asks for —
   * never the net position.
   */
  external_credit_outstanding?: string;
  external_credit_direction?: SettlementPositionDirection;
  /** REQUESTED payment requests now (not as of `to`): count and Σ outstanding. */
  payment_requests_open?: SettlementPaymentRequestsOpen;
  /** Part of clearing_balance: money in users' open positions, at cost. */
  users_open_position_cost?: string;
  /**
   * Sent with users_open_position_cost (SM-912): the cost always counts
   * toward "the broker owes us", so BROKER_OWES_US above 0, SETTLED at 0.
   */
  users_open_position_direction?: SettlementPositionDirection;
  /** Part of clearing_balance: users' settled results and fees (= clearing_balance + open cost). */
  users_settled_result?: string;
  users_settled_result_direction?: SettlementPositionDirection;
}

export interface SettlementPaymentRequestsOpen {
  count: number;
  outstanding_amount: string;
}

/** A ROOT's unresolved receivable (OPEN / ACKNOWLEDGED); null on a child node. */
export interface SettlementReceivableRef {
  receivable_id: string;
  amount: string;
  state: string;
}

export interface SettlementFeeFigures {
  traded_value: string;
  exchange_fee: string;
  broker_commission: string;
}

export interface SettlementPositionBranch {
  broker_id: string;
  display_name: string;
  broker_depth: number;
  status: string;
  has_descendants: boolean;
  figures: SettlementPositionFigures;
}

export interface SettlementPositionResponse {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  display_name: string;
  broker_depth: number;
  status: string;
  currency: string;
  scope: SettlementScope;
  from_unix_ms: number;
  to_unix_ms: number;
  own: SettlementPositionFigures;
  consolidated?: SettlementPositionFigures;
  branches?: SettlementPositionBranch[];
  fees: SettlementFeeFigures;
  outstanding_caption: string;
  note_caption: string;
  /** The root's unresolved receivable; null (or absent) on a child node. */
  open_receivable?: SettlementReceivableRef | null;
}

export interface SettlementPositionListItem {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  display_name: string;
  broker_depth: number;
  status: string;
  has_descendants: boolean;
  figures: SettlementPositionFigures;
  /** The root's unresolved receivable; null (or absent) on a child node. */
  open_receivable?: SettlementReceivableRef | null;
}

export interface SettlementPositionListResponse {
  currency: string;
  scope: SettlementScope;
  from_unix_ms: number;
  to_unix_ms: number;
  sort: string;
  items: SettlementPositionListItem[];
  page: PageInfo;
  outstanding_caption: string;
}

export interface SettlementNoteItem {
  note_id: string;
  broker_id: string;
  direction: SettlementNoteDirection;
  amount: string;
  currency: string;
  status: SettlementNoteStatus;
  value_date: string;
  external_reference: string;
  note: string;
  supersedes_note_id?: string;
  superseded_by_note_id?: string;
  author_id?: string;
  created_at_unix_ms: number;
  confirmed_by_id?: string;
  confirmed_at_unix_ms?: number;
  captions: string[];
  /** The payment request an INBOUND note pays (SM-878 S4); a correction inherits it. */
  payment_request_id?: string;
  /**
   * The receivable a PENDING or APPROVED settle uses this note for
   * (SM-948), and that settle's state. Admin routes only.
   */
  settles_receivable_id?: string;
  settles_receivable_state?: "PENDING" | "APPROVED";
}

/** The confirm answer: the note, and whether this confirmation made its request PAID. */
export interface ConfirmSettlementNoteResponse extends SettlementNoteItem {
  payment_request_paid?: boolean;
}

export interface SettlementNoteListResponse {
  items: SettlementNoteItem[];
  page: PageInfo;
}

// --- duplicate bank references (SM-948; GET /admin/brokers/
// settlement-references). A warning only: live notes and PENDING or
// APPROVED receivable settles that already use a reference, compared
// with case and spaces ignored, at most 10 of each, newest first. ---

export interface SettlementReferenceNoteMatch {
  note_id: string;
  broker_id: string;
  broker_display_name: string;
  direction: SettlementNoteDirection;
  amount: string;
  currency: string;
  status: SettlementNoteStatus;
  value_date: string;
}

export interface SettlementReferenceSettleMatch {
  receivable_id: string;
  action_id: string;
  broker_id: string;
  broker_display_name: string;
  state: "PENDING" | "APPROVED";
  amount: string;
  currency: string;
  requested_at_unix_ms: number;
}

export interface SettlementReferenceMatchesResponse {
  notes: SettlementReferenceNoteMatch[];
  receivable_settles: SettlementReferenceSettleMatch[];
}

export interface RecordSettlementNoteRequest {
  direction: SettlementNoteDirection;
  amount: string;
  currency: string;
  value_date: string;
  external_reference?: string;
  note?: string;
  supersedes_note_id?: string;
  /**
   * INBOUND only. Omitted on a correction: the server inherits the
   * original's link, and a different id is 409.
   */
  payment_request_id?: string;
}

// --- payment requests (SM-878 S4 / SM-883; contracts httpapi/v1/
// broker_payment_requests.go). One admin records that the broker was
// asked for an amount; it is REQUESTED until linked confirmed INBOUND
// notes cover it (PAID), or CANCELLED with a reason. Nothing moves money. ---

export type PaymentRequestStatus = "REQUESTED" | "PAID" | "CANCELLED";

export const PAYMENT_REQUEST_STATUSES: PaymentRequestStatus[] = ["REQUESTED", "PAID", "CANCELLED"];

export interface PaymentRequestItem {
  request_id: string;
  broker_id: string;
  broker_display_name: string;
  currency: string;
  /** Canonical 8dp, what was asked for. */
  amount: string;
  /** Σ linked CONFIRMED INBOUND notes. */
  received_amount: string;
  /** max(0, amount − received_amount), the server's. */
  outstanding_amount: string;
  /** YYYY-MM-DD in the exchange's operating calendar, or empty. */
  due_date?: string;
  reference: string;
  note: string;
  status: PaymentRequestStatus;
  requested_by_id?: string;
  cancelled_by_id?: string;
  cancel_reason?: string;
  created_at_unix_ms: number;
  paid_at_unix_ms?: number;
  captions?: string[];
}

export interface PaymentRequestListResponse {
  items: PaymentRequestItem[];
  page: PageInfo;
}

export interface CreatePaymentRequestRequest {
  currency: string;
  amount: string;
  due_date?: string;
  reference?: string;
  note?: string;
}

/** POST /admin/brokers/{id}/payout-batches — an operator's manual batch. */
export interface CreatePayoutBatchRequest {
  currency: string;
  reason: string;
}

// ── external-credit provider configuration (SM-634, SM-621 phase 11) ──
//
// contracts httpapi/v1/broker_provider.go. Every shape is REDACTED by
// construction: there is no field that can hold secret material — the
// credential is `credential_ref`, a Secret Manager secret-version
// resource name, never a value.

export type ProviderEnvironment = "sandbox" | "production";

export const PROVIDER_ENVIRONMENTS: ProviderEnvironment[] = ["sandbox", "production"];

/** contracts brokerv1.ProviderReason — the closed money-operation reason set. */
export type ProviderReason =
  | "SMART_MARKET_ORDER_FUNDING"
  | "SMART_MARKET_UNUSED_ORDER_FUNDS"
  | "SMART_MARKET_TRADE_PROCEEDS"
  | "SMART_MARKET_REDEMPTION_PAYOUT"
  | "SMART_MARKET_ORDER_FUNDING_COMPENSATION"
  | "SMART_MARKET_CREDIT_COMPENSATION";

export const PROVIDER_REASONS: ProviderReason[] = [
  "SMART_MARKET_ORDER_FUNDING",
  "SMART_MARKET_UNUSED_ORDER_FUNDS",
  "SMART_MARKET_TRADE_PROCEEDS",
  "SMART_MARKET_REDEMPTION_PAYOUT",
  "SMART_MARKET_ORDER_FUNDING_COMPENSATION",
  "SMART_MARKET_CREDIT_COMPENSATION",
];

/** contracts brokerv1.ProviderEnableRule, in evaluation order. */
export type ProviderEnableRule =
  | "BROKER_NOT_ACTIVE"
  | "MODE_NOT_CAPABLE"
  | "BALANCE_MODE_NOT_ENABLED"
  | "CONTRACT_NOT_APPROVED"
  | "ENVIRONMENT_MISMATCH"
  | "CREDENTIAL_UNRESOLVED"
  | "EXPOSURE_LIMIT_MISSING"
  | "FLAG_OFF"
  | "PROVIDER_MODE_DISABLED";

export const PROVIDER_ENABLE_RULES: ProviderEnableRule[] = [
  "BROKER_NOT_ACTIVE",
  "MODE_NOT_CAPABLE",
  "BALANCE_MODE_NOT_ENABLED",
  "CONTRACT_NOT_APPROVED",
  "ENVIRONMENT_MISMATCH",
  "CREDENTIAL_UNRESOLVED",
  "EXPOSURE_LIMIT_MISSING",
  "FLAG_OFF",
  "PROVIDER_MODE_DISABLED",
];

export type ProviderConfigEventKind =
  | "CREATED"
  | "UPDATED"
  | "ENABLED"
  | "DISABLED"
  | "ENABLE_REFUSED"
  | "CREDENTIAL_ROTATED"
  | "CREDENTIAL_CHECKED"
  | "CREDENTIAL_UPLOADED";

/** Per-broker overrides; null inherits the environment default. */
export interface ProviderConfigKnobs {
  connect_timeout_ms: number | null;
  request_deadline_ms: number | null;
  read_retry_backoff_ms: number[] | null;
  deduct_capacity_rps: number | null;
  snapshot_max_age_s: number | null;
}

/** The values in force: override, else default. */
export interface ProviderEffectiveKnobs {
  connect_timeout_ms: number;
  request_deadline_ms: number;
  read_retry_backoff_ms: number[];
  deduct_capacity_rps: number;
  snapshot_max_age_s: number;
}

export interface ProviderConfigView {
  broker_id: string;
  launch_slug: string;
  launch_slug_locked: boolean;
  api_base_url: string;
  client_id: string;
  credential_ref: string;
  environment: ProviderEnvironment | string;
  enabled: boolean;
  reason_allowlist: (ProviderReason | string)[];
  contract_version: string;
  approved_contract_sha: string;
  overrides: Partial<ProviderConfigKnobs>;
  effective: ProviderEffectiveKnobs;
  row_version: number;
  updated_by: string;
  updated_at_unix_ms: number;
}

export interface ProviderEnableBlocker {
  rule: ProviderEnableRule | string;
  message: string;
}

export interface ProviderEnablement {
  ready: boolean;
  blockers: ProviderEnableBlocker[];
}

export interface ProviderConfigResponse {
  broker_id: string;
  configured: boolean;
  config?: ProviderConfigView;
  enablement: ProviderEnablement;
  provider_mode: string;
  /** This process's FF_THIRD_PARTY_PROVIDER. */
  third_party_provider: boolean;
  environment_label: string;
  /**
   * Whether this deployment can store a pasted secret key (SM-919). On the
   * response, not on config: the first upload happens before a
   * configuration exists. fetchProviderConfig fills it in when absent.
   */
  credential_upload: ProviderCredentialUploadAvailability;
  /**
   * This deployment's right values for the fields an operator never types
   * (SM-933): the values the enable rules accept, so a configuration saved
   * with them never fails ENVIRONMENT_MISMATCH or CONTRACT_NOT_APPROVED.
   * Present even when configured=false; absent from a broker-service
   * older than SM-935.
   */
  defaults?: ProviderConfigDefaults;
}

export interface ProviderConfigDefaults {
  environment: ProviderEnvironment;
  contract_version: string;
  approved_contract_sha: string;
  reason_allowlist: ProviderReason[];
}

/**
 * available = PROVIDER_SECRET_WRITE_ENABLED and a Secret Manager backend;
 * secret_id_prefix = "broker-provider-<environment_label>-", the start of
 * every secret id an upload may write.
 */
export interface ProviderCredentialUploadAvailability {
  available: boolean;
  secret_id_prefix: string;
}

/**
 * POST …/provider-config/credential-upload (BROKER_PROVIDER_SECRET_WRITE,
 * SM-919): the ONE request body in this console that carries secret
 * material. value is the key exactly as pasted — never trimmed, never
 * decoded — and nothing ever sends it back.
 */
export interface ProviderCredentialUploadRequest {
  launch_slug: string;
  value: string;
  reason: string;
}

/** The upload's 201: metadata only. credential_ref is pinned to the stored version. */
export interface ProviderCredentialUploadResponse {
  credential_ref: string;
  secret_id: string;
  secret_version: string;
  secret_created: boolean;
  byte_length: number;
  reason: string;
}

// ── provider key versions (SM-639; SM-926 / SM-930) ──
//
// contracts httpapi/v1/broker_launch.go. A broker's provider HMAC key is a
// set of versions, each a Secret Manager REFERENCE — never a value.
// Exactly one is ACTIVE_SIGNING (signs every call); OVERLAP verifies but
// never signs (a staged new key, or the previous key for 7 days); RETIRED
// and REVOKED do neither. The console shows plain labels for these; the
// codes appear only under Technical details.

export type ProviderCredentialState = "ACTIVE_SIGNING" | "OVERLAP" | "REVOKED" | "RETIRED";
export type ProviderCredentialAction = "ROTATE" | "ACTIVATE" | "REVOKE" | "ROLLBACK" | "RETIRE";

export interface ProviderCredentialVersion {
  broker_id: string;
  key_version: number;
  environment: ProviderEnvironment;
  credential_ref: string;
  state: ProviderCredentialState;
  /** RFC 3339. */
  valid_from: string;
  /** RFC 3339; set on an OVERLAP version that will retire, a RETIRED or a REVOKED one. */
  valid_to: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProviderCredentialEvent {
  event_id: string;
  broker_id: string;
  key_version: number;
  action: ProviderCredentialAction;
  from_state?: ProviderCredentialState | "";
  to_state: ProviderCredentialState;
  /** "<KIND>:<id>", or "SYSTEM" for a retirement nobody triggered. */
  actor: string;
  reason: string;
  request_id?: string;
  occurred_at: string;
}

/** GET …/provider-credentials (BROKER_PROVIDER_VIEW): versions and events newest first. */
export interface ProviderCredentialListResponse {
  broker_id: string;
  active_signing_version: number | null;
  /** RFC 3339: when the key in use is due to be changed (every 90 days); null without one. */
  rotation_due_at: string | null;
  versions: ProviderCredentialVersion[];
  events: ProviderCredentialEvent[];
}

/** POST …/provider-credentials/rotate (CONFIGURE): stage an existing Secret Manager version. */
export interface ProviderCredentialRotateRequest {
  credential_ref: string;
  reason: string;
}

/**
 * POST …/provider-credentials/upload (BROKER_PROVIDER_SECRET_WRITE, SM-926):
 * store a pasted key and stage it while the provider stays enabled. Like
 * ProviderCredentialUploadRequest it carries the key, exactly as pasted,
 * and nothing sends it back. No slug: the server uses the saved one.
 */
export interface ProviderCredentialUploadStageRequest {
  value: string;
  reason: string;
}

/** The stage-by-upload's 200: the fresh list plus what was stored. Metadata only. */
export interface ProviderCredentialUploadStageResponse extends ProviderCredentialListResponse {
  upload: {
    secret_id: string;
    secret_version: string;
    byte_length: number;
    secret_created: boolean;
    staged_key_version: number;
  };
}

// ── batched provider launch read (SM-903) ──
// contracts httpapi/v1/broker_provider.go ProviderLaunchListResponse.

/**
 * Why a configured broker's launch URL answers the constant 404 even with
 * launch on in the process — the broker-level half of broker-service's
 * slug gate. "" means ready.
 */
export type ProviderLaunchNotReadyReason = "" | "DISABLED" | "SYSTEM_BROKER" | "BROKER_DRAFT" | "BROKER_CLOSED";

/** One configured broker's launch identity (a subset of ProviderConfigView). */
export interface ProviderLaunchItem {
  broker_id: string;
  launch_slug: string;
  launch_slug_locked: boolean;
  enabled: boolean;
  environment: ProviderEnvironment | string;
  launch_ready: boolean;
  not_ready_reason: ProviderLaunchNotReadyReason;
}

/**
 * GET /admin/brokers/provider-launch?broker_ids=a,b (BROKER_PROVIDER_VIEW).
 * launch_base_urls are "scheme://host", one per launch host; the URL a
 * provider registers is base + "/launch/" + slug with no query — the
 * provider appends its own one-time ?code=. Nothing here carries a code.
 */
export interface ProviderLaunchListResponse {
  launch_active: boolean;
  launch_base_urls: string[];
  items: ProviderLaunchItem[];
}

export interface ProviderConfigUpsertRequest extends ProviderConfigKnobs {
  launch_slug: string;
  api_base_url: string;
  client_id: string;
  credential_ref: string;
  environment: ProviderEnvironment;
  reason_allowlist: ProviderReason[];
  contract_version: string;
  approved_contract_sha: string;
  expected_row_version?: number;
  reason: string;
}

export interface ProviderEnablementRequest {
  enabled: boolean;
  reason: string;
}

/** The rotation drill: the reference re-resolved, metadata only. */
export interface ProviderCredentialCheckResponse {
  broker_id: string;
  credential_ref: string;
  resolved: boolean;
  resolved_version?: string;
  error_code?: string;
  checked_at_unix_ms: number;
}

export interface ProviderConfigEvent {
  event_id: string;
  broker_id: string;
  kind: ProviderConfigEventKind | string;
  actor: string;
  reason: string;
  /** Non-secret fields only (broker-service's contract). */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  occurred_at_unix_ms: number;
}

export interface ProviderConfigEventListResponse {
  items: ProviderConfigEvent[];
  page: PageInfo;
}

// ── provider launch audit (SM-643, SM-621 phase 12) ─────────────────
//
// contracts httpapi/v1/broker_launch.go LaunchAuditListResponse. A
// launch decision never holds the launch code, the provider's user id or
// any personal value — provider_identity_id is Smart Market's own id for
// the mapped identity.

/** contracts brokerv1.LaunchDecision. */
export type LaunchDecision = "ALLOW" | "ALLOW_RESTRICTED" | "DENY";

/** contracts brokerv1.LaunchDenyClass — the only thing about a refusal that leaves broker-service. */
export type LaunchDenyClass = "INVALID_OR_EXPIRED" | "ACCOUNT_UNAVAILABLE" | "PROVIDER_UNAVAILABLE";

/** Where the decision was taken. */
export type LaunchStage = "SLUG" | "EXCHANGE" | "VALIDATE" | "MAPPING" | "ONBOARDING" | "POLICY" | "SESSION";

/** contracts brokerv1.ProviderRetryClass — the provider client's class for the exchange call. */
export type ProviderRetryClass =
  | "committed"
  | "definitive_failure"
  | "not_executed"
  | "retryable_read"
  | "ambiguous_money"
  | "contract_violation";

export interface LaunchAuditEvent {
  event_id: string;
  broker_id: string;
  provider_identity_id?: string;
  decision: LaunchDecision | string;
  deny_class?: LaunchDenyClass | string;
  stage: LaunchStage | string;
  /** "" / absent when no provider call was made. */
  outcome_class?: ProviderRetryClass | string;
  provider_code?: string;
  request_id: string;
  correlation_id?: string;
  key_version?: number;
  /** RFC 3339. */
  occurred_at: string;
}

/** The wire page: newest first, with a total and no page envelope. */
export interface LaunchAuditListResponse {
  items: LaunchAuditEvent[];
  total: number;
}

// ── provider-operation recovery cases (SM-661, SM-621 phase 14) ─────
//
// contracts httpapi/v1/broker_provider_recovery.go and the vocabulary in
// contracts broker/v1/funding_recovery.go. A case is the durable record
// of a provider operation (or a funding intent) automation could not
// finish safely on its own. Operators act on it only through the six
// server-checked actions; none takes an amount, a balance or a
// transaction id — the request body is a note and nothing else.

/** contracts brokerv1.RecoveryCaseKind — why the case was opened. */
export type RecoveryCaseKind =
  | "CONTRACT_VIOLATION"
  | "UNRESOLVED_ESCALATED"
  | "ROLLBACK_NOT_ALLOWED"
  | "CREDIT_FAILED"
  | "STRANDED_PAST_DEADLINE"
  | "LATE_COMMIT_AFTER_RELEASE"
  | "CONSERVATION";

export const RECOVERY_CASE_KINDS: RecoveryCaseKind[] = [
  "CONTRACT_VIOLATION",
  "UNRESOLVED_ESCALATED",
  "ROLLBACK_NOT_ALLOWED",
  "CREDIT_FAILED",
  "STRANDED_PAST_DEADLINE",
  "LATE_COMMIT_AFTER_RELEASE",
  "CONSERVATION",
];

/** CRITICAL pages, HIGH queues. Fixed per kind. */
export type RecoveryCaseSeverity = "CRITICAL" | "HIGH";
export const RECOVERY_CASE_SEVERITIES: RecoveryCaseSeverity[] = ["CRITICAL", "HIGH"];

/** OPEN → RESOLVED; a case is never deleted. */
export type RecoveryCaseState = "OPEN" | "RESOLVED";
export const RECOVERY_CASE_STATES: RecoveryCaseState[] = ["OPEN", "RESOLVED"];

/** contracts brokerv1.RecoveryAction — the path segment of the action route. */
export type RecoveryAction =
  | "status-check"
  | "retry-identical"
  | "finalize-local"
  | "deliver-credit"
  | "rollback"
  | "resolve";

/** brokerv1.AllRecoveryActions(), in the contract's order. */
export const RECOVERY_ACTIONS: RecoveryAction[] = [
  "status-check",
  "retry-identical",
  "finalize-local",
  "deliver-credit",
  "rollback",
  "resolve",
];

/** The owner queues (brokerv1.RecoveryQueue*). */
export type RecoveryOwnerQueue = "FINANCE_OPS" | "PROVIDER_INTEGRATION";

/** contracts ExternalFundingItem — the order's funding intent, when the case has one. */
export interface ExternalFundingItem {
  intent_id: string;
  order_id: string;
  client_order_id?: string;
  user_id: string;
  broker_id: string;
  broker_account_id: string;
  side: string;
  currency: string;
  required_funding_canonical: string;
  /** Provider decimals, four fractional digits. */
  required_funding_provider: string;
  state: string;
  resolution: string;
  provider_transaction_id?: string;
  reject_code?: string;
  provider_code?: string;
  order_notified: boolean;
  release_reason?: string;
  released_at?: string;
  /** The deadline a stranded release must resolve by (SM-793). */
  deadline_at?: string;
  created_at: string;
  updated_at: string;
}

/** contracts ProviderOperationItem — one provider money operation. */
export interface ProviderOperationItem {
  transaction_id: string;
  broker_id: string;
  broker_account_id: string;
  /** DEDUCT / CREDIT / ROLLBACK. */
  kind: string;
  reason: string;
  amount: string;
  business_ref_kind: string;
  business_ref_id: string;
  original_transaction_id?: string;
  fingerprint: string;
  /** PENDING / IN_FLIGHT / UNKNOWN / COMMITTED / FAILED / NOT_ALLOWED. */
  local_state: string;
  provider_status?: string;
  failure_code?: string;
  attempts: number;
  first_attempt_at?: string;
  last_attempt_at?: string;
  committed_at?: string;
  next_recovery_at?: string;
  created_at: string;
  /** Every provider request of the operation, oldest first — sent on the case detail only (SM-661). */
  attempt_log?: ProviderOperationAttemptItem[];
}

/**
 * contracts ProviderOperationAttemptItem — one provider request of an
 * operation. Never the signature, nonce, secret or the provider's user id.
 */
export interface ProviderOperationAttemptItem {
  attempt_id: string;
  /** contracts brokerv1.ProviderEndpoint, e.g. /wallet/v1/Deduct or /wallet/v1/GetTransactionStatus. */
  endpoint: string;
  request_id: string;
  key_version?: string;
  environment?: string;
  outcome_class: ProviderRetryClass | string;
  /** Absent when no HTTP response arrived (a timeout or transport failure). */
  http_status?: number;
  provider_code?: string;
  /** The transaction status the provider answered on this attempt, when it answered one. */
  provider_status?: string;
  latency_ms: number;
  started_at: string;
  finished_at: string;
}

/** ACCEPTED (started / queued), REFUSED (eligibility failed; nothing ran) or DONE (a read finished). */
export type RecoveryActionResult = "ACCEPTED" | "REFUSED" | "DONE";

/** contracts RecoveryActionItem — one audited operator action. */
export interface RecoveryActionItem {
  action_id: string;
  case_id: string;
  admin_id: string;
  action: RecoveryAction | string;
  target_transaction_id?: string;
  result: RecoveryActionResult | string;
  detail?: string;
  note?: string;
  request_id?: string;
  created_at: string;
}

/** contracts RecoveryCaseItem. */
export interface RecoveryCaseItem {
  case_id: string;
  kind: RecoveryCaseKind | string;
  severity: RecoveryCaseSeverity | string;
  state: RecoveryCaseState | string;
  owner_queue: RecoveryOwnerQueue | string;
  /** A runbook key (brokerv1.RecoveryCaseKind.RecoveryPath). */
  recovery_path: string;
  broker_id: string;
  broker_account_id: string;
  currency: string;
  intent_id?: string;
  order_id?: string;
  transaction_id?: string;
  /** What was known when the case opened. Never a secret, signature or provider user id. */
  evidence: unknown;
  /** The actions broker-service would accept NOW; the console disables the rest. */
  eligible_actions: RecoveryAction[] | null;
  opened_at: string;
  resolved_at?: string;
  resolution_action?: string;
  resolution_note?: string;
  funding?: ExternalFundingItem;
  operation?: ProviderOperationItem;
  actions?: RecoveryActionItem[];
}

export interface RecoveryCaseListResponse {
  items: RecoveryCaseItem[];
  page: PageInfo;
}

/** The body of an action: a note, and nothing that could name money. */
export interface RecoveryActionRequest {
  note?: string;
}

/** The action's audit row and the case after it. */
export interface RecoveryActionResponse {
  case: RecoveryCaseItem;
  action: RecoveryActionItem;
}

// ── broker fee plans, assignments and resolution (SM-839, SM-840) ────
//
// Wire mirrors of contracts httpapi/v1/broker_fees.go and the fee
// schedule of broker_portal.go, with the vocabulary of contracts
// broker/v1/fees.go. RATES are integer strings at fee_rate_precision —
// always 6 for broker plans ("10000" = 1 %, "1000000" = 100 %); times
// are unix ms. Every plan is owned by ONE root; a version covers ONE
// currency (GOLD, RED or GREEN) and carries a redemption rate only in
// GOLD. Versions and assignments are immutable: a change is a new
// version, or a newer assignment, with its own effective time. Which
// plan a node pays is ALWAYS read from broker-service (the fee schedule
// and the fee resolution) — the console never walks the tree itself.

/** Where a root-owned plan is attached in its tree. */
export type FeeAssignmentScope = "GROUP" | "BRANCH" | "NODE";

/** The scopes as the picker offers them: the widest first. */
export const FEE_ASSIGNMENT_SCOPES: FeeAssignmentScope[] = ["GROUP", "BRANCH", "NODE"];

/** One immutable, currency-specific version of a plan. */
export interface FeePlanVersionItem {
  /** What an order locks as its fee_plan_id. */
  plan_version_id: string;
  plan_id: string;
  root_broker_id: string;
  /** 1, 2, … per plan and currency. */
  version: number;
  currency: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  redemption_fee_rate: string;
  fee_rate_precision: number;
  effective_at_unix_ms: number;
  published_by: string;
  published_at_unix_ms: number;
}

/** One root-owned plan. Names are not unique and never change. */
export interface FeePlanItem {
  plan_id: string;
  root_broker_id: string;
  name: string;
  /** Only the seeded SYSTEM_BROKER zero plan, which every publish path refuses. */
  protected?: boolean;
  /** The plan is usable at t iff unset or > t. */
  retired_at_unix_ms?: number;
  created_by: string;
  created_at_unix_ms: number;
  /** Set on the detail read only. */
  versions?: FeePlanVersionItem[];
  /**
   * Detail read only (SM-851): the currencies the root and its non-CLOSED
   * descendants trade, GOLD, RED, GREEN order — the only ones a new
   * version may be published in (BROKER_FEE_CURRENCY_NOT_TRADED
   * otherwise). Versions in other currencies stay listed as history.
   * Since SM-868: ["GOLD"] on every broker tree, all three on the
   * SYSTEM_BROKER's (tradedCurrencies applies the same when absent).
   */
  tree_currencies?: string[];
}

export interface FeePlanListResponse {
  items: FeePlanItem[];
  page: PageInfo;
}

export interface CreateFeePlanRequest {
  name: string;
  reason: string;
}

/** effective_at_unix_ms 0 / omitted = now; a past time is refused (400). */
export interface PublishFeePlanVersionRequest {
  currency: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  /** "0" for RED and GREEN: redemption fees exist on the GOLD path only. */
  redemption_fee_rate: string;
  effective_at_unix_ms?: number;
  reason: string;
}

/** retire_at_unix_ms 0 / omitted = now. A plan retires once. */
export interface RetireFeePlanRequest {
  retire_at_unix_ms?: number;
  reason: string;
}

/** One immutable assignment. No plan_id = a CLEARED rule from effective_at. */
export interface FeeAssignmentItem {
  assignment_id: string;
  root_broker_id: string;
  scope: FeeAssignmentScope;
  /** The root for GROUP. */
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms: number;
  reason: string;
  published_by: string;
  published_at_unix_ms: number;
}

/** Newest first. The list has no filter: it is history, not "the current plan". */
export interface FeeAssignmentListResponse {
  items: FeeAssignmentItem[];
  page: PageInfo;
}

/**
 * Attaches a plan at a scope — or, with no plan_id, clears that scope's
 * rule so resolution falls back up the path. The same (scope, target,
 * effective time) twice is refused BROKER_FEE_ASSIGNMENT_CONFLICT.
 */
export interface PublishFeeAssignmentRequest {
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms?: number;
  reason: string;
}

/** GET /admin/broker-accounts/{id}/fee-resolution — what an order from that account locks. */
export interface FeeResolution {
  broker_account_id: string;
  broker_id: string;
  root_broker_id: string;
  broker_path_snapshot_id: string;
  currency: string;
  fee_plan_id: string;
  fee_plan_version_id: string;
  fee_plan_version: number;
  fee_owner_broker_id: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  redemption_fee_rate: string;
  fee_rate_precision: number;
  assignment_id: string;
  assignment_scope: FeeAssignmentScope;
  assignment_target_broker_id: string;
  revenue_share_plan_id: string;
  resolved_at_unix_ms: number;
}

/**
 * GET /admin/brokers/{id}/fee-schedule?currency= (any node): the nearest
 * active assignment on the node's current path, its plan and current
 * version, the later scheduled versions and assignments, and the node's
 * own share under the revenue-share version an order would lock now.
 * The same body as the portal's /fees/schedule.
 */
export interface StaffFeeScheduleResponse {
  broker_id: string;
  root_broker_id: string;
  currency: string;
  /** Absent = no plan resolves: orders here are refused BROKER_FEE_ASSIGNMENT_MISSING. */
  assignment?: FeeAssignmentItem;
  plan?: FeePlanItem;
  current?: FeePlanVersionItem;
  upcoming: FeePlanVersionItem[];
  upcoming_assignments: FeeAssignmentItem[];
  /** "rsv_default_servicing_100" = the default split, 100 % to the servicing broker. */
  revenue_share_plan_version_id?: string;
  node_share_bps: number;
  resolved_at_unix_ms: number;
  /**
   * Why no plan resolves (SM-837), only when none does: NO_ASSIGNMENT,
   * PLAN_RETIRED, NO_VERSION_IN_CURRENCY, PLAN_UNKNOWN or
   * PLAN_OWNER_MISMATCH — the refusal's details.rule.
   */
  missing_rule?: string;
}

// ── broker revenue-share plans (SM-841) ──────────────────────────────
//
// Wire mirrors of contracts httpapi/v1/broker_revenue.go (share plans),
// the fee-plan model applied to the split of the broker commission: a
// plan owned by ONE root, immutable versions (shares in bps totalling
// exactly 10 000 and a remainder recipient, effective from a time), and
// assignments at the same GROUP / BRANCH / NODE scopes. A recipient is a
// broker id in the root's tree or the "@servicing" token (the node that
// services the order). Unlike fee rules, share rules FALL BACK: a
// retired plan, or one with no version yet, is skipped, down to the
// default split (100 % to the servicing broker).

/** One recipient of a version. */
export interface RevenueShare {
  /** A broker id in the root's tree, or "@servicing". */
  recipient: string;
  share_bps: number;
}

/** One immutable share version. */
export interface RevenueSharePlanVersionItem {
  plan_version_id: string;
  plan_id: string;
  /** "" for the built-in default. */
  root_broker_id?: string;
  version: number;
  shares: RevenueShare[];
  remainder_recipient: string;
  effective_at_unix_ms: number;
  published_by: string;
  published_at_unix_ms: number;
  reason?: string;
  /** The built-in default version (never stored). */
  default?: boolean;
}

/** One root-owned share plan. */
export interface RevenueSharePlanItem {
  plan_id: string;
  root_broker_id: string;
  name: string;
  retired_at_unix_ms?: number;
  created_by: string;
  created_at_unix_ms: number;
  /** The version effective now; absent when none is yet. */
  current_version_id?: string;
}

export interface RevenueSharePlanListResponse {
  items: RevenueSharePlanItem[];
  page: PageInfo;
}

/** One plan with every version, newest first. */
export interface RevenueSharePlanDetail {
  plan: RevenueSharePlanItem;
  versions: RevenueSharePlanVersionItem[];
}

export interface CreateRevenueSharePlanRequest {
  name: string;
  reason: string;
}

/** effective_at_unix_ms 0 / omitted = now; a past time is refused. */
export interface PublishRevenueShareVersionRequest {
  shares: RevenueShare[];
  remainder_recipient: string;
  effective_at_unix_ms?: number;
  reason: string;
}

/** retire_at_unix_ms 0 / omitted = now. A plan retires once. */
export interface RetireRevenueSharePlanRequest {
  retire_at_unix_ms?: number;
  reason: string;
}

/** One immutable share assignment. No plan_id = cleared (the walk goes on from effective_at). */
export interface RevenueShareAssignmentItem {
  assignment_id: string;
  root_broker_id: string;
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms: number;
  reason?: string;
  published_by: string;
  published_at_unix_ms: number;
}

export interface RevenueShareAssignmentListResponse {
  items: RevenueShareAssignmentItem[];
  page: PageInfo;
}

/** Attaches (or, with no plan_id, clears) a share plan at a scope from effective_at (0 = now). */
export interface PublishRevenueShareAssignmentRequest {
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms?: number;
  reason: string;
}
